import { useEffect, useRef, useState } from "react";
import { Camera, Trash2, User, Phone, Mail, Save, ShieldCheck, ShieldAlert, Lock, Loader2, Globe, MapPin, Building2, Briefcase, MessageCircle, Facebook, Instagram, Linkedin, Twitter, X } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MyPageShell from "../components/MyPageShell";

// India states + top cities — used for the searchable datalist inputs below.
const INDIA_STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Chandigarh","Jammu & Kashmir","Ladakh"];
const POPULAR_CITIES = ["Nagpur","Mumbai","Pune","Nashik","Aurangabad","Thane","Navi Mumbai","Delhi","Gurgaon","Noida","Bengaluru","Hyderabad","Chennai","Kolkata","Ahmedabad","Surat","Jaipur","Lucknow","Kanpur","Indore","Bhopal","Chandigarh","Patna","Ranchi","Bhubaneswar","Kochi","Coimbatore","Vadodara","Visakhapatnam","Raipur"];

const USER_TYPES = [
    { v: "buyer", label: "Buyer / Tenant" },
    { v: "seller", label: "Seller / Owner" },
    { v: "broker", label: "Broker / Property Agent" },
    { v: "builder", label: "Builder / Developer" },
];

/**
 * Comprehensive Edit Profile — replaces the old thin Account Settings.
 * Single page, single save button per form. Uses existing PUT /api/me + new
 * POST /api/me/upload-photo + DELETE /api/me/photo endpoints.
 */
export default function AccountSettings() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);
    const fileRef = useRef(null);

    // Password fields
    const [cur, setCur] = useState("");
    const [np, setNp] = useState("");
    const [np2, setNp2] = useState("");
    const [changing, setChanging] = useState(false);

    useEffect(() => {
        if (!user) return;
        setForm({
            name: user.name || "",
            phone: user.phone || "",
            whatsapp: user.whatsapp || user.phone || "",
            city: user.city || "",
            state: user.state || "",
            locality: user.locality || "",
            role: USER_TYPES.some((t) => t.v === user.role) ? user.role : "",
            company: user.company || "",
            designation: user.designation || "",
            bio: user.bio || "",
            website: user.website || "",
            social_links: user.social_links || { facebook: "", instagram: "", linkedin: "", twitter: "" },
            photo: user.photo || "",
        });
    }, [user]);

    if (!user || !form) return null;

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const isPartner = user.role === "gpm_partner";
    const isServicePro = ["advocate", "loan_agent", "architect", "interior_designer", "civil_contractor", "civil_engineer", "property_valuer", "renovation"].includes(user.role);
    const kycStatus = (user.kyc_status || (user.verified ? "verified" : "not_submitted")).toLowerCase();
    const kycBadge = {
        verified: { label: "Verified", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30", icon: ShieldCheck },
        pending: { label: "Under Review", cls: "bg-amber-500/15 text-amber-700 border-amber-500/30", icon: Loader2 },
        rejected: { label: "Rejected — Resubmit", cls: "bg-rose-500/15 text-rose-700 border-rose-500/30", icon: ShieldAlert },
        not_submitted: { label: "Not Submitted", cls: "bg-slate-500/15 text-slate-600 border-slate-500/30", icon: ShieldAlert },
    }[kycStatus] || { label: kycStatus, cls: "bg-slate-500/15 text-slate-600 border-slate-500/30", icon: ShieldAlert };

    const saveProfile = async (e) => {
        e?.preventDefault();
        if (!form.name.trim()) return toast.error("Full name is required");
        if (form.phone && !/^[6-9]\d{9}$/.test(form.phone)) return toast.error("Phone must be a valid 10-digit Indian number");
        if (form.whatsapp && !/^[6-9]\d{9}$/.test(form.whatsapp)) return toast.error("WhatsApp must be a valid 10-digit Indian number");
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim() || null,
                whatsapp: form.whatsapp.trim() || null,
                city: form.city.trim() || null,
                state: form.state.trim() || null,
                locality: form.locality.trim() || null,
                company: form.company.trim() || null,
                designation: form.designation.trim() || null,
                bio: form.bio.trim() || null,
                website: form.website.trim() || null,
                social_links: form.social_links,
            };
            // Only allow role update if the current role is in the self-editable list.
            if (USER_TYPES.some((t) => t.v === user.role) && form.role) payload.role = form.role;
            await api.put("/me", payload);
            if (refresh) await refresh();
            toast.success("Profile saved ✓");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
        } finally { setSaving(false); }
    };

    const onPickPhoto = () => fileRef.current?.click();
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return toast.error("Max 5 MB");
        setPhotoBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post("/me/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set({ photo: r.data.photo });
            if (refresh) await refresh();
            toast.success("Photo updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally { setPhotoBusy(false); e.target.value = ""; }
    };
    const removePhoto = async () => {
        if (!window.confirm("Remove profile photo?")) return;
        setPhotoBusy(true);
        try {
            await api.delete("/me/photo");
            set({ photo: "" });
            if (refresh) await refresh();
            toast.success("Photo removed");
        } catch { toast.error("Could not remove"); }
        finally { setPhotoBusy(false); }
    };

    const changePw = async (e) => {
        e.preventDefault();
        if (np !== np2) return toast.error("New passwords do not match");
        if (np.length < 6) return toast.error("Password must be at least 6 characters");
        setChanging(true);
        try {
            await api.post("/me/change-password", { current_password: cur, new_password: np });
            toast.success("Password changed ✓");
            setCur(""); setNp(""); setNp2("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to change password");
        } finally { setChanging(false); }
    };

    return (
        <MyPageShell title="Edit Profile" subtitle="Keep your information up to date">
            <div className="space-y-5 pb-8" data-testid="edit-profile">

                {/* ---------- 1. PROFILE PHOTO ---------- */}
                <Card testid="section-photo">
                    <SectionTitle>Profile Photo</SectionTitle>
                    <div className="flex items-center gap-4">
                        <div className="relative shrink-0">
                            {form.photo ? (
                                <img src={fileUrl(form.photo)} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2" style={{ borderColor: "#DAA520" }} />
                            ) : (
                                <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-heading font-extrabold border-2" style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)", color: "#001a3d", borderColor: "#DAA520" }} data-testid="photo-placeholder">
                                    {(user.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 flex flex-wrap gap-2">
                            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="photo-input" />
                            <button type="button" onClick={onPickPhoto} disabled={photoBusy} data-testid="btn-upload-photo" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-bold" style={{ backgroundColor: "#002657", color: "#fff" }}>
                                {photoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                {form.photo ? "Change" : "Upload"}
                            </button>
                            {form.photo && (
                                <button type="button" onClick={removePhoto} disabled={photoBusy} data-testid="btn-remove-photo" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-bold border" style={{ borderColor: "rgba(220,38,38,0.3)", color: "#DC2626", backgroundColor: "#fff" }}>
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                </button>
                            )}
                            <p className="w-full text-[11px] mt-1" style={{ color: "rgba(0,38,87,0.55)" }}>JPG / PNG / WEBP · Max 5 MB</p>
                        </div>
                    </div>
                </Card>

                {/* ---------- 2. BASIC INFO ---------- */}
                <Card testid="section-basic">
                    <SectionTitle>Basic Information</SectionTitle>
                    <form onSubmit={saveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Full Name" required>
                            <input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="input-name" className={inputCls} placeholder="Your name" />
                        </Field>
                        <Field label="Email" hint="Verified — not editable">
                            <div className="flex items-center gap-2">
                                <input value={user.email} disabled className={`${inputCls} opacity-70`} data-testid="input-email" />
                                <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: "#059669" }} />
                            </div>
                        </Field>
                        <Field label="Phone Number">
                            <input inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => set({ phone: e.target.value.replace(/\D/g, "") })} data-testid="input-phone" className={inputCls} placeholder="10-digit mobile" />
                        </Field>
                        <Field label="WhatsApp Number">
                            <input inputMode="numeric" maxLength={10} value={form.whatsapp} onChange={(e) => set({ whatsapp: e.target.value.replace(/\D/g, "") })} data-testid="input-whatsapp" className={inputCls} placeholder="Same as phone if unsure" />
                        </Field>
                        <Field label="State">
                            <input list="in-states" value={form.state} onChange={(e) => set({ state: e.target.value })} data-testid="input-state" className={inputCls} placeholder="Type or pick" />
                            <datalist id="in-states">{INDIA_STATES.map((s) => <option key={s} value={s} />)}</datalist>
                        </Field>
                        <Field label="City">
                            <input list="in-cities" value={form.city} onChange={(e) => set({ city: e.target.value })} data-testid="input-city" className={inputCls} placeholder="Type or pick" />
                            <datalist id="in-cities">{POPULAR_CITIES.map((c) => <option key={c} value={c} />)}</datalist>
                        </Field>
                        <Field label="Locality / Area" full>
                            <input value={form.locality} onChange={(e) => set({ locality: e.target.value })} data-testid="input-locality" className={inputCls} placeholder="e.g. Wardha Road, Manish Nagar" />
                        </Field>

                        {/* User type — role selector (only shown for self-editable roles) */}
                        {USER_TYPES.some((t) => t.v === user.role) ? (
                            <Field label="User Type" full>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {USER_TYPES.map((t) => (
                                        <button
                                            key={t.v}
                                            type="button"
                                            onClick={() => set({ role: t.v })}
                                            data-testid={`role-${t.v}`}
                                            className={`h-10 px-3 rounded-lg text-xs font-bold border transition-colors ${form.role === t.v ? "bg-gold-500 border-gold-500 text-navy-950" : "bg-white text-navy-500 border-navy-500/15 hover:border-gold-500/50"}`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                        ) : (
                            <Field label="Account Role" full hint={isPartner ? "GPM Partner accounts cannot change role — please contact admin." : (isServicePro ? "Service-professional roles need admin approval to change." : "")}>
                                <div className="h-10 px-3 rounded-lg border flex items-center text-sm capitalize" style={{ backgroundColor: "#F1F5F9", borderColor: "rgba(0,38,87,0.15)", color: "#002657" }} data-testid="role-display">
                                    {(user.role || "user").replace(/_/g, " ")}
                                </div>
                            </Field>
                        )}

                        {/* Business fields */}
                        <Field label="Company / Business Name">
                            <div className="relative">
                                <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(0,38,87,0.4)" }} />
                                <input value={form.company} onChange={(e) => set({ company: e.target.value })} data-testid="input-company" className={`${inputCls} pl-8`} placeholder="Optional" />
                            </div>
                        </Field>
                        <Field label="Designation">
                            <div className="relative">
                                <Briefcase className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(0,38,87,0.4)" }} />
                                <input value={form.designation} onChange={(e) => set({ designation: e.target.value })} data-testid="input-designation" className={`${inputCls} pl-8`} placeholder="e.g. Founder, Senior Broker" />
                            </div>
                        </Field>
                        <Field label="About / Bio" full>
                            <textarea rows={3} value={form.bio} onChange={(e) => set({ bio: e.target.value })} data-testid="input-bio" className={`${inputCls} resize-y`} placeholder="Tell clients about you — experience, specialisation, service areas…" />
                        </Field>
                        <Field label="Website" full>
                            <div className="relative">
                                <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(0,38,87,0.4)" }} />
                                <input type="url" value={form.website} onChange={(e) => set({ website: e.target.value })} data-testid="input-website" className={`${inputCls} pl-8`} placeholder="https://yourbusiness.com" />
                            </div>
                        </Field>

                        <div className="sm:col-span-2">
                            <div className="text-[10px] uppercase tracking-widest font-extrabold mb-2 mt-1" style={{ color: "rgba(0,38,87,0.55)" }}>Social Links (optional)</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <SocialInput icon={Facebook} value={form.social_links.facebook} onChange={(v) => set({ social_links: { ...form.social_links, facebook: v } })} placeholder="Facebook profile URL" testid="input-fb" />
                                <SocialInput icon={Instagram} value={form.social_links.instagram} onChange={(v) => set({ social_links: { ...form.social_links, instagram: v } })} placeholder="Instagram profile URL" testid="input-ig" />
                                <SocialInput icon={Linkedin} value={form.social_links.linkedin} onChange={(v) => set({ social_links: { ...form.social_links, linkedin: v } })} placeholder="LinkedIn profile URL" testid="input-li" />
                                <SocialInput icon={Twitter} value={form.social_links.twitter} onChange={(v) => set({ social_links: { ...form.social_links, twitter: v } })} placeholder="Twitter / X profile URL" testid="input-tw" />
                            </div>
                        </div>

                        <div className="sm:col-span-2 flex justify-end mt-1">
                            <button type="submit" disabled={saving} data-testid="btn-save-profile" className="inline-flex items-center gap-2 h-11 px-6 rounded-xl font-extrabold text-sm shadow" style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
                            </button>
                        </div>
                    </form>
                </Card>

                {/* ---------- 3. KYC STATUS (READ-ONLY) ---------- */}
                <Card testid="section-kyc">
                    <SectionTitle>KYC Verification Status</SectionTitle>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-extrabold border ${kycBadge.cls}`} data-testid="kyc-badge">
                            <kycBadge.icon className={`w-3.5 h-3.5 ${kycStatus === "pending" ? "animate-spin" : ""}`} />
                            {kycBadge.label}
                        </div>
                        <a href="/kyc" data-testid="kyc-link" className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: "#002657" }}>
                            {kycStatus === "verified" ? "View documents" : "Submit / Update KYC →"}
                        </a>
                    </div>
                    <p className="text-[11px] mt-2" style={{ color: "rgba(0,38,87,0.55)" }}>KYC status is set by the GPM admin team after document review — it cannot be changed from here.</p>
                </Card>

                {/* ---------- 4. CHANGE PASSWORD ---------- */}
                <Card testid="section-password">
                    <SectionTitle>Change Password</SectionTitle>
                    <form onSubmit={changePw} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Field label="Current Password">
                            <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="input-current-pw" className={inputCls} placeholder="••••••••" required />
                        </Field>
                        <Field label="New Password">
                            <input type="password" value={np} onChange={(e) => setNp(e.target.value)} data-testid="input-new-pw" className={inputCls} placeholder="Min 6 characters" required />
                        </Field>
                        <Field label="Confirm New Password">
                            <input type="password" value={np2} onChange={(e) => setNp2(e.target.value)} data-testid="input-new-pw2" className={inputCls} placeholder="Repeat" required />
                        </Field>
                        <div className="sm:col-span-3 flex justify-end">
                            <button type="submit" disabled={changing} data-testid="btn-change-pw" className="inline-flex items-center gap-2 h-10 px-5 rounded-lg text-sm font-extrabold" style={{ backgroundColor: "#002657", color: "#fff" }}>
                                {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Update Password
                            </button>
                        </div>
                    </form>
                </Card>
            </div>
        </MyPageShell>
    );
}

/* ---------- Small building blocks ---------- */
const inputCls = "w-full h-10 px-3 rounded-lg border bg-white text-sm outline-none focus:ring-2 focus:ring-gold-500/40 transition-shadow";
function Field({ label, hint, required, full, children }) {
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`}>
            <span className="block text-[10px] uppercase tracking-widest font-extrabold mb-1" style={{ color: "rgba(0,38,87,0.6)" }}>
                {label} {required && <span style={{ color: "#DC2626" }}>*</span>}
            </span>
            {children}
            {hint && <span className="block text-[10px] mt-1" style={{ color: "rgba(0,38,87,0.45)" }}>{hint}</span>}
        </label>
    );
}
function SectionTitle({ children }) {
    return <h3 className="font-heading font-extrabold text-base mb-3" style={{ color: "#002657" }}>{children}</h3>;
}
function Card({ testid, children }) {
    return <div data-testid={testid} className="rounded-2xl bg-white p-4 sm:p-5 shadow-sm border" style={{ borderColor: "rgba(0,38,87,0.06)" }}>{children}</div>;
}
function SocialInput({ icon: Icon, value, onChange, placeholder, testid }) {
    return (
        <div className="relative">
            <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(0,38,87,0.4)" }} />
            <input type="url" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`${inputCls} pl-8`} placeholder={placeholder} />
        </div>
    );
}
